import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { PrimaryDropdownProps, PrimaryDropdownRef } from './PrimaryDropdown.data';
import styles from './PrimaryDropdown.module.scss';
import {
  openDropdown,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './PrimaryDropdown.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useScrollTransition } from 'util/react-transition-component';
import PrimaryLink from '../primary-link/PrimaryLink';
import Text from '../text/Text';
import useToggle from '../../../hooks/useToggle';
import Icon from '../icon/Icon';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import { setupHoverTransitionTimeline } from '../primary-button/PrimaryButton.transitions';
import { defaultSize } from './PrimaryDropdown.data';
import gsap from 'gsap';

export default function PrimaryDropdown({
  isVisible,
  filters,
  filterLabels,
  onFilterClick,
  className,
  activeFilter,
  canTransitionIn,
  type,
  disableTriggerInOnScroll,
  textVariant = 'body-standard',
  size = defaultSize,
  ref,
}: PrimaryDropdownProps & { ref?: Ref<PrimaryDropdownRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const listRef = useRef<HTMLUListElement>(null);
  const labelRef = useRef<HTMLParagraphElement>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const hoverLabelRef = useRef<HTMLSpanElement>(null);
  const hoverTimeline = useRef<gsap.core.Timeline>(undefined);

  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [isOpen, toggleOpen] = useToggle(false);
  const selectedFilter = activeFilter || filters[0];
  const selectedLabel = filterLabels?.[selectedFilter] || selectedFilter;

  const { transitionController } = useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        labelRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);

  hoverTimeline.current = useSetupTimeline(setupHoverTransitionTimeline, {
    backgroundHoverRef,
    hoverLabelRef,
    labelRef,
  });

  const onMouseLeave = useCallback(() => {
    if (!isOpen) {
      hoverTimeline?.current?.reverse();
    }
  }, [hoverTimeline, isOpen]);

  const onMouseEnter = useCallback(() => {
    if (!isOpen) {
      hoverTimeline?.current?.play();
    }
  }, [hoverTimeline, isOpen]);

  useOnClickOutside(elementRef, () => {
    if (isOpen) {
      toggleOpen();
    }
  });

  function onFilterSelect(filter: string): void {
    toggleOpen();
    onFilterClick(filter, type);
  }

  useEffect(() => {
    openDropdown(listRef, isOpen);

    if (!isOpen) {
      hoverTimeline?.current?.reverse();
    }
  }, [isOpen]);

  useEffect(() => {
    const width = gsap.getProperty(dropdownWrapperRef.current, 'width');
    gsap.set(buttonRef.current, { width });
  }, []);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.primaryDropdown, className, styles[`size-${size}`], {
        [styles.isOpen]: isOpen,
      })}
    >
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        type="button"
        className={classNames(styles.selector, 'button')}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Text ref={labelRef} as="p" size={textVariant} className={classNames(styles.selectedValue)}>
          {selectedLabel}
        </Text>
        <Text
          ref={hoverLabelRef}
          transitionInType="splitCharsNone"
          className={classNames(styles.selectedValue, styles.hoverLabel)}
          as="span"
          size={textVariant}
        >
          {selectedLabel}
        </Text>
      </button>

      <div ref={dropdownWrapperRef} className={classNames(styles.wrapper)}>
        <Icon name="chevron-down" className={classNames(styles.icon)} />
        <div ref={backgroundHoverRef} className={classNames(styles.backgroundHover)} />

        <ul ref={listRef} className={classNames(styles.list)}>
          {filters.map((filter) => (
            <li key={filter} className={classNames(styles.listItem)}>
              <PrimaryLink
                textVariant={textVariant}
                className={classNames(styles.button, {
                  [styles.isActive]: activeFilter === filter,
                })}
                label={filterLabels?.[filter] || filter}
                onClick={() => onFilterSelect(filter)}
              />
            </li>
          ))}
        </ul>
        <div className={classNames(styles.border)} />
      </div>
    </div>
  );
}
